import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SwapPoolTabs } from '../../components/NavigationTabs'
import AppBody from '../AppBody'
import OrderForm from './OrderForm'
import OrderList from './OrderList'
import OrderBookView from './OrderBookView'
import OrderBookAdmin from './OrderBookAdmin'
import OrderHelpModal from './OrderHelpModal'
import CancelOrderModal from './CancelOrderModal'
import UpdatePriceModal from './UpdatePriceModal'
import OrderFillsModal from './OrderFillsModal'
import TransactionConfirmationModal from '../../components/TransactionConfirmationModal'
import { useUserOrders, useCancelOrder, useUpdateOrderPrice, useHasOrderBookRole } from '../../hooks/useOrderBook'
import { OrderType, OrderStatus } from '../../constants/orderbook'
import { TabButton, TabRow } from './styleds'

export type OrderStatusFilter = number | undefined // undefined = 全部

export type OrderTabKey = 'orderbook' | 'mine' | 'admin'

export default function Order() {
  const { t } = useTranslation()
  const { isAdmin, isExecutor } = useHasOrderBookRole()
  const showAdminTab = isAdmin || isExecutor

  // Tab
  const [orderTab, setOrderTab] = useState<OrderTabKey>('orderbook')

  // Pagination (shared for all/mine lists)
  const [pageSize] = useState(10)
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<OrderStatusFilter>(OrderStatus.OPEN)
  const [typeFilter, setTypeFilter] = useState<number | undefined>(undefined)
  const { orders: myOrders, total: myTotal, loading: myLoading, refresh: refreshMy } = useUserOrders(
    page,
    pageSize,
    statusFilter,
    typeFilter
  )
  const orders = myOrders
  const total = myTotal
  const loading = myLoading
  const refresh = refreshMy

  const [isManualRefreshing, setIsManualRefreshing] = useState(false)
  useEffect(() => {
    if (!loading) setIsManualRefreshing(false)
  }, [loading])

  // Cancel modal
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [cancelOrderId, setCancelOrderId] = useState<number>(0)
  const { cancelOrder, pending: cancelPending } = useCancelOrder()
  const [cancellingId, setCancellingId] = useState<number | null>(null)

  // Update price modal
  const [updateModalOpen, setUpdateModalOpen] = useState(false)
  const [updateOrderId, setUpdateOrderId] = useState<number>(0)
  const [updateCurrentPrice, setUpdateCurrentPrice] = useState('')
  const { updatePrice, pending: updatePending } = useUpdateOrderPrice()

  // Fills (detail) modal
  const [fillsModalOpen, setFillsModalOpen] = useState(false)
  const [fillsOrderId, setFillsOrderId] = useState<number | null>(null)
  const [fillsOrderIsBuy, setFillsOrderIsBuy] = useState(true)

  // 交易已提交弹窗（取消订单 / 修改价格 成功后展示，参考 Blockscout 样式）
  const [txConfirmOpen, setTxConfirmOpen] = useState(false)
  const [txConfirmPending, setTxConfirmPending] = useState(false)
  const [txConfirmHash, setTxConfirmHash] = useState<string | undefined>(undefined)
  const [txConfirmPendingText, setTxConfirmPendingText] = useState('')
  const [balanceRefreshTrigger, setBalanceRefreshTrigger] = useState(0)
  const [orderBookRefreshTrigger, setOrderBookRefreshTrigger] = useState(0)
  const [helpModalOpen, setHelpModalOpen] = useState(false)

  const refreshOrderBookAndMy = useCallback(() => {
    refreshMy()
    setOrderBookRefreshTrigger(t => t + 1)
  }, [refreshMy])

  // Order created callback：立即刷新订单簿 + 我的订单
  const handleOrderCreated = useCallback(() => {
    refreshOrderBookAndMy()
    setTimeout(refreshOrderBookAndMy, 2000)
  }, [refreshOrderBookAndMy])

  // Cancel handlers
  const handleCancelClick = useCallback((orderId: number) => {
    setCancelOrderId(orderId)
    setCancelModalOpen(true)
  }, [])

  const handleCancelConfirm = useCallback(async () => {
    const orderIdToCancel = cancelOrderId
    setCancelModalOpen(false)
    setTxConfirmOpen(true)
    setTxConfirmPending(true)
    setTxConfirmHash(undefined)
    setTxConfirmPendingText(t('orderCancelling'))
    try {
      setCancellingId(orderIdToCancel)
      const tx = await cancelOrder(orderIdToCancel)
      if (tx) {
        setTxConfirmHash(tx.hash)
        setTxConfirmPending(false)
        setBalanceRefreshTrigger(prev => prev + 1)
        tx.wait()
          .then(() => {
            refreshOrderBookAndMy()
            setTimeout(refreshOrderBookAndMy, 1500)
          })
          .catch(() => {})
      } else {
        setTxConfirmOpen(false)
      }
    } catch (e) {
      console.error('Cancel failed:', e)
      setTxConfirmOpen(false)
    } finally {
      setCancellingId(null)
    }
  }, [cancelOrderId, cancelOrder, refreshOrderBookAndMy, t])

  // Update price handlers
  const handleUpdatePriceClick = useCallback((orderId: number, currentPrice: string) => {
    setUpdateOrderId(orderId)
    setUpdateCurrentPrice(currentPrice)
    setUpdateModalOpen(true)
  }, [])

  const handleUpdatePriceConfirm = useCallback(
    async (newPrice: string) => {
      setUpdateModalOpen(false)
      setTxConfirmOpen(true)
      setTxConfirmPending(true)
      setTxConfirmHash(undefined)
      setTxConfirmPendingText(t('orderUpdating'))
      try {
        const tx = await updatePrice(updateOrderId, newPrice)
        if (tx) {
          setTxConfirmHash(tx.hash)
          setTxConfirmPending(false)
          tx.wait()
            .then(() => {
              refreshOrderBookAndMy()
              setTimeout(refreshOrderBookAndMy, 1500)
            })
            .catch(() => {})
        } else {
          setTxConfirmOpen(false)
        }
      } catch (e) {
        console.error('Update price failed:', e)
        setTxConfirmOpen(false)
      }
    },
    [updateOrderId, updatePrice, refreshOrderBookAndMy, t]
  )

  const handleDetailClick = useCallback((orderId: number, isBuy?: boolean) => {
    setFillsOrderId(orderId)
    setFillsOrderIsBuy(
      isBuy !== undefined ? isBuy : (orders.find(o => o.orderId === orderId)?.type === OrderType.BUY ?? true)
    )
    setFillsModalOpen(true)
  }, [orders])

  const handleTabChange = useCallback(
    (tab: OrderTabKey) => {
      setOrderTab(tab)
      setPage(1)
      if (tab === 'mine') refreshMy()
      // 切到订单簿时 OrderBookView 挂载，useOrderBookDepth 的 useEffect 会自动 fetch，无需再传 trigger 避免重复请求
    },
    [refreshMy]
  )

  const totalPages = total > 0 ? Math.ceil(total / pageSize) : 0
  const handlePageChange = useCallback(
    (nextPage: number) => {
      setPage(Math.max(1, Math.min(nextPage, totalPages || 1)))
    },
    [totalPages]
  )

  const handleStatusFilterChange = useCallback((status: OrderStatusFilter) => {
    setStatusFilter(status)
    setPage(1)
  }, [])

  const handleTypeFilterChange = useCallback((type: number | undefined) => {
    setTypeFilter(type)
    setPage(1)
  }, [])

  const handleTxConfirmDismiss = useCallback(() => {
    setTxConfirmOpen(false)
    setTxConfirmHash(undefined)
  }, [])

  return (
    <>
      <AppBody>
        <SwapPoolTabs active={'order'} />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            fontSize: 13,
            color: 'var(--text2)',
            lineHeight: 1.4,
            marginTop: -15,
            marginBottom: 15,
            padding: '0 0.75rem',
            flexWrap: 'wrap'
          }}
        >
          <span style={{ flex: '1 1 0', minWidth: 0 }}>{t('orderHelpSummary')}</span>
          <button
            type="button"
            onClick={() => setHelpModalOpen(true)}
            style={{
              flexShrink: 0,
              padding: 0,
              border: 'none',
              background: 'none',
              color: '#1890ff',
              cursor: 'pointer',
              fontSize: 'inherit'
            }}
          >
            {t('orderHelpDetail')}
          </button>
        </div>
        <OrderForm onOrderCreated={handleOrderCreated} balanceRefreshTrigger={balanceRefreshTrigger} />

        <TabRow>
          <TabButton type="button" active={orderTab === 'orderbook'} onClick={() => handleTabChange('orderbook')}>
            {t('orderTabOrderBook')}
          </TabButton>
          <TabButton type="button" active={orderTab === 'mine'} onClick={() => handleTabChange('mine')}>
            {t('orderTabMyOrders')}
          </TabButton>
          {showAdminTab && (
            <TabButton type="button" active={orderTab === 'admin'} onClick={() => handleTabChange('admin')}>
              {t('orderTabManageContract')}
            </TabButton>
          )}
        </TabRow>

        {orderTab === 'admin' ? (
          <OrderBookAdmin
            isAdmin={isAdmin}
            isExecutor={isExecutor}
            onDetail={handleDetailClick}
            onCancel={handleCancelClick}
            onUpdatePrice={handleUpdatePriceClick}
            cancellingId={cancellingId}
          />
        ) : orderTab === 'orderbook' ? (
          <OrderBookView refreshTrigger={orderBookRefreshTrigger} />
        ) : (
          <OrderList
            orders={orders}
            total={total}
            page={page}
            pageSize={pageSize}
            loading={loading}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
            onPageChange={handlePageChange}
            onCancel={handleCancelClick}
            onUpdatePrice={handleUpdatePriceClick}
            onDetail={handleDetailClick}
            cancellingId={cancellingId}
            onRefresh={() => {
              setIsManualRefreshing(true)
              setBalanceRefreshTrigger(prev => prev + 1)
              refresh()
            }}
            isRefreshing={isManualRefreshing}
            listTitleKey="orderMyOrders"
            showStatusFilter={true}
            showTypeFilterInRow={true}
            typeFilter={typeFilter}
            onTypeFilterChange={handleTypeFilterChange}
          />
        )}
      </AppBody>

      <CancelOrderModal
        isOpen={cancelModalOpen}
        orderId={cancelOrderId}
        onDismiss={() => setCancelModalOpen(false)}
        onConfirm={handleCancelConfirm}
        pending={cancelPending}
      />

      <UpdatePriceModal
        isOpen={updateModalOpen}
        orderId={updateOrderId}
        currentPrice={updateCurrentPrice}
        onDismiss={() => setUpdateModalOpen(false)}
        onConfirm={handleUpdatePriceConfirm}
        pending={updatePending}
      />

      <OrderFillsModal
        isOpen={fillsModalOpen}
        onDismiss={() => setFillsModalOpen(false)}
        orderId={fillsOrderId}
        isBuy={fillsOrderIsBuy}
      />

      <TransactionConfirmationModal
        isOpen={txConfirmOpen}
        onDismiss={handleTxConfirmDismiss}
        hash={txConfirmHash}
        attemptingTxn={txConfirmPending}
        pendingText={txConfirmPendingText}
        content={() => null}
      />

      <OrderHelpModal isOpen={helpModalOpen} onDismiss={() => setHelpModalOpen(false)} />
    </>
  )
}
